import { useMemo, useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, BookOpenCheck, FileText, FolderClosed, GitBranch } from 'lucide-react'
import { Chip } from '../hybrid'
import { getDocumentKindInfo } from '../../lib/document-kind'
import { formatNumber } from './home-workspace-knowledge-primitives'
import {
  workspaceDocPath,
  type ProjectTreeNode,
  type WorkspaceKnowledgeProject,
} from './home-workspace-project-tree-data'

function TreeNode({ node, depth }: { node: ProjectTreeNode; depth: number }) {
  const style = { '--tree-depth': `${depth * 14}px` } as CSSProperties

  if (node.doc) {
    const kind = getDocumentKindInfo(node.doc)
    const Icon =
      kind.kind === 'verified' ? BookOpenCheck : kind.kind === 'draft' ? FileText : AlertTriangle
    return (
      <Link
        to={workspaceDocPath(node.doc)}
        className={`aw-project-tree-file is-${kind.kind}`}
        style={style}
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{node.name}</span>
        <small>{kind.shortLabel}</small>
      </Link>
    )
  }

  return (
    <div className="aw-project-tree-folder">
      <div className="aw-project-tree-folder-label" style={style}>
        <FolderClosed className="h-3.5 w-3.5" />
        <span>{node.name}</span>
      </div>
      {node.children.map((child) => (
        <TreeNode key={child.id} node={child} depth={depth + 1} />
      ))}
    </div>
  )
}

export function WorkspaceProjectTree({
  projects,
  isLoading,
}: {
  projects: WorkspaceKnowledgeProject[]
  isLoading?: boolean
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedId) ?? projects[0],
    [projects, selectedId],
  )

  return (
    <section className="aw-workspace-panel aw-workspace-project-map">
      <div className="aw-workspace-panel-head">
        <div>
          <span className="aw-label">Project map</span>
          <h3>Projects and nested source tree</h3>
        </div>
        <Chip tone={projects.length > 0 ? 'proc' : 'mut'}>
          {isLoading ? 'loading' : formatNumber(projects.length)}
        </Chip>
      </div>

      {isLoading ? (
        <div className="aw-project-tree-skeleton" />
      ) : selectedProject ? (
        <div className="aw-project-map-layout">
          <div className="aw-project-card-list" aria-label="Knowledge projects">
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                className={project.id === selectedProject.id ? 'active' : undefined}
                aria-pressed={project.id === selectedProject.id}
                onClick={() => setSelectedId(project.id)}
              >
                <strong>{project.label}</strong>
                <span>{project.subtitle}</span>
                <small>
                  {project.verifiedCount} source-backed · {project.reviewCount} review ·{' '}
                  {project.docs.length} docs
                </small>
              </button>
            ))}
          </div>

          <div className="aw-project-tree" aria-label={`${selectedProject.label} source tree`}>
            <div className="aw-project-tree-top">
              <div>
                <strong>{selectedProject.label}</strong>
                <span>{selectedProject.subtitle}</span>
              </div>
              <GitBranch className="h-4 w-4" aria-hidden />
            </div>
            <div className="aw-project-tree-list">
              {selectedProject.root.map((node) => (
                <TreeNode key={node.id} node={node} depth={0} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="aw-workspace-empty">
          No project tree yet. Sync repo docs or source-backed files to build a nested knowledge
          map.
        </div>
      )}
    </section>
  )
}
